import type {
  ApprovalChainPortalDto,
  CollectionItemStatus,
  PortalPackageSummaryDto,
} from "../api/types";

/**
 * The shape the progress predicates read. `approvalChain` is the caller-
 * relative chain view (ruling R-C); when it is present it — not `status` —
 * decides whether the item needs the caller and whether it is done.
 */
export interface ProgressItem {
  status: CollectionItemStatus;
  approvalChain?: Pick<ApprovalChainPortalDto, "myStatus" | "isCompleted"> | null;
}

// Post-Rollout PR 2 (PROB-13 F.4) — shared canonical completion helpers
// used by the progress meter, the Continue CTA, and the checklist UI so
// they can never disagree on "what counts as done." PROB-11 was the
// original symptom: the meter and the checklist used different rules
// and showed different counts.

// Post-Rollout PR 4 (PROB-28c) — the lifecycle states the spec describes
// (NotStarted / Submitted / UnderReview / NeedsChanges / Filed) map onto
// the real CollectionItemStatus enum as follows; there is no separate
// Submitted-vs-UnderReview or Filed state on the backend, so no migration:
//   NotStarted   -> Pending
//   InProgress   -> InProgress
//   Submitted    -> SubmittedForReview
//   UnderReview  -> SubmittedForReview
//   NeedsChanges -> ChangesRequested
//   Approved     -> Approved
//   Filed        -> Approved / Waived (terminal, staff-decided)

/**
 * "Complete" / done definition for the progress meter.
 *
 * Returns true ONLY for the terminal staff decisions — Approved or Waived.
 * "Under review" (SubmittedForReview) is deliberately NOT complete: the
 * recipient has handed it off but the work isn't finished, so the segmented
 * breakdown shows it as its own distinct "under review" segment rather than
 * folding it into "complete". This matches the backend package-level rule
 * (PortalPackageService.IsCompleted = Approved || Waived), so the item-level
 * meter and the package summary can no longer contradict each other.
 *
 * NOTE: "complete" and "actionable by the recipient" are different ideas —
 * use isRecipientActionable for "what does the user still need to do?"
 * (e.g. the Continue CTA), never the negation of this predicate, or you'll
 * route the recipient to an under-review item they can't touch.
 */
export function isCollectionItemComplete(item: ProgressItem): boolean {
  // Approval chains — a chain item is done once every party has acted
  // (isCompleted). A party whose own stage is Done is NOT complete while the
  // chain still waits on others; it is merely not actionable (below). A
  // staff terminal decision (Approved / Waived) still ends it.
  if (item.approvalChain && item.approvalChain.isCompleted) return true;
  return item.status === "Approved" || item.status === "Waived";
}

/**
 * Recipient-actionable definition: the item is waiting on the recipient to
 * do something — Pending (not started), InProgress (resume), or
 * ChangesRequested (fix & resubmit). False for SubmittedForReview (with
 * staff), Approved, and Waived (done). Drives the Continue CTA and the
 * "all caught up" collapse so the recipient is never sent to an item they
 * cannot act on, and an item merely under review still lets "all caught
 * up" show.
 */
export function isRecipientActionable(item: ProgressItem): boolean {
  // Approval chains (ruling R-C) — only the caller's own turn needs them.
  // Waiting (an earlier party has not acted) and Done (they already have)
  // never do, whatever the item's status says.
  if (item.approvalChain) {
    return item.approvalChain.myStatus === "YourTurn" && !isCollectionItemComplete(item);
  }
  return (
    item.status === "Pending" ||
    item.status === "InProgress" ||
    item.status === "ChangesRequested"
  );
}

/**
 * Lifecycle tab routing (PROB-39).
 *
 * Items live in the Requests (Outstanding) tab while in-flight — Pending,
 * InProgress, SubmittedForReview, ChangesRequested. On staff transition to
 * a terminal decision (Approved or Waived) the item moves to the Documents
 * tab. Both tab queries consult this helper.
 *
 * Confirmed by user during UAT walkthrough 2026-05-28:
 *   "Once a request is completed and approved, that record and the document
 *    they uploaded would THEN move from the requests tab to the document tab."
 */
export function getItemTabBucket(
  item: { status: CollectionItemStatus },
): "requests" | "documents" {
  return item.status === "Approved" || item.status === "Waived"
    ? "documents"
    : "requests";
}

export function isPackageComplete(
  pkg: Pick<PortalPackageSummaryDto, "status" | "totalItems" | "completedItems">,
): boolean {
  if (pkg.status === "Completed") return true;
  return pkg.totalItems > 0 && pkg.completedItems >= pkg.totalItems;
}

export interface CompletionTally {
  done: number;
  total: number;
  pct: number;
}

export function packagesTally(packages: PortalPackageSummaryDto[]): CompletionTally {
  const total = packages.reduce((s, p) => s + p.totalItems, 0);
  const done = packages.reduce((s, p) => s + p.completedItems, 0);
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return { done, total, pct };
}

/**
 * Item-level tally for the top-of-dashboard progress meter
 * (PortalProgressBreakdown). Counts done items (see isCollectionItemComplete
 * — Approved/Waived only) over the total item count across all packages.
 * Pass the union of the Requests + Documents buckets so already-approved
 * items count toward the denominator.
 */
export function itemsTally(items: ProgressItem[]): CompletionTally {
  const total = items.length;
  const done = items.filter(isCollectionItemComplete).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return { done, total, pct };
}

/**
 * Pick the item the "Continue" card should point at: the first
 * recipient-actionable item (see isRecipientActionable), sorted by due-date
 * soonest-first then last-updated newest-first. Returns null when nothing
 * needs the recipient — including the case where the only remaining items
 * are under review, so the dashboard collapses to "all caught up".
 */
export function nextOutstandingItem<
  T extends ProgressItem & { dueDate: string | null; lastUpdatedUtc: string },
>(items: T[]): T | null {
  const outstanding = items.filter(isRecipientActionable);
  if (outstanding.length === 0) return null;
  return [...outstanding].sort((a, b) => {
    const aDue = a.dueDate ? Date.parse(a.dueDate) : Number.POSITIVE_INFINITY;
    const bDue = b.dueDate ? Date.parse(b.dueDate) : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    return Date.parse(b.lastUpdatedUtc) - Date.parse(a.lastUpdatedUtc);
  })[0];
}

/**
 * Pick the package the "Continue" card should point at: the first
 * package with outstanding items, sorted by due-date soonest-first
 * then by sentAt newest-first. Returns null when nothing's outstanding.
 */
export function nextOutstandingPackage(
  packages: PortalPackageSummaryDto[],
): PortalPackageSummaryDto | null {
  const outstanding = packages.filter((p) => !isPackageComplete(p));
  if (outstanding.length === 0) return null;
  return [...outstanding].sort((a, b) => {
    const aDue = a.dueDate ? Date.parse(a.dueDate) : Number.POSITIVE_INFINITY;
    const bDue = b.dueDate ? Date.parse(b.dueDate) : Number.POSITIVE_INFINITY;
    if (aDue !== bDue) return aDue - bDue;
    const aSent = a.sentAt ? Date.parse(a.sentAt) : 0;
    const bSent = b.sentAt ? Date.parse(b.sentAt) : 0;
    return bSent - aSent;
  })[0];
}
